import { Injectable } from '@angular/core';
import { BehaviorSubject, of } from 'rxjs';
import { Expense, GroupedExpense } from '../expense.service';

@Injectable({
    providedIn: 'root'
})
export class MockExpenseService {
    // In-memory store
    private expenses: Expense[] = [];

    // Observables matching the real service
    private groupedExpensesSubject = new BehaviorSubject<GroupedExpense[]>([]);
    groupedExpenses$ = this.groupedExpensesSubject.asObservable();

    private currentDateSubject = new BehaviorSubject<string>(new Date().toISOString().split('T')[0]);
    currentDate$ = this.currentDateSubject.asObservable();

    setCurrentDate(date: string) {
        this.currentDateSubject.next(date);
    }

    getCurrentDateValue(): string {
        return this.currentDateSubject.value;
    }

    // Internal store with type
    private allCategories: { id: string, name: string, type: 'family' | 'trip' }[] = [
        { id: '1', name: 'Food', type: 'family' },
        { id: '2', name: 'Travel', type: 'family' },
        { id: '3', name: 'Utilities', type: 'family' }
    ];

    private categoriesSubject = new BehaviorSubject<{ id: string, name: string }[]>(this.allCategories.map(c => ({ id: c.id, name: c.name })));
    categories$ = this.categoriesSubject.asObservable();

    // Category Management Methods
    async getCategoriesForType(type: 'family' | 'trip'): Promise<{ id: string, name: string }[]> {
        return this.allCategories.filter(c => c.type === type).map(c => ({ id: c.id, name: c.name }));
    }

    async addCategory(name: string, type: 'family' | 'trip') {
        const newCat = { id: 'mock-cat-' + Date.now(), name, type };
        this.allCategories.push(newCat);
        this.categoriesSubject.next(this.allCategories.map(c => ({ id: c.id, name: c.name })));
        return Promise.resolve();
    }

    async updateCategory(id: string, name: string, type: 'family' | 'trip') {
        this.allCategories = this.allCategories.map(c => c.id === id ? { ...c, name } : c);
        this.categoriesSubject.next(this.allCategories.map(c => ({ id: c.id, name: c.name })));
        return Promise.resolve();
    }

    async deleteCategory(id: string, type: 'family' | 'trip') {
        this.allCategories = this.allCategories.filter(c => c.id !== id);
        this.categoriesSubject.next(this.allCategories.map(c => ({ id: c.id, name: c.name })));
        return Promise.resolve();
    }

    isCategoryInUse(name: string): boolean {
        // Mock impl: check if any expense uses it
        return this.expenses.some(e => e.category === name);
    }

    constructor() {
        this.updateGroupedExpenses();
    }

    getToday(): string {
        return new Date().toISOString().split('T')[0];
    }

    getGroupedExpensesValue() {
        return this.groupedExpensesSubject.value;
    }

    setContext(type: 'family' | 'trip', id: string) {
        console.log(`MockExpenseService context set to ${type}: ${id}`);
    }

    async addExpense(expense: any) {
        const newExpense: Expense = {
            id: 'mock-expense-' + Math.random().toString(36).substr(2, 9),
            ...expense,
            createdAt: new Date()
        };
        this.expenses.push(newExpense);
        this.updateGroupedExpenses();
        return Promise.resolve(newExpense);
    }

    async deleteExpense(id: string) {
        this.expenses = this.expenses.filter(e => e.id !== id);
        this.updateGroupedExpenses();
        return Promise.resolve();
    }

    async updateExpense(id: string, data: any) {
        const index = this.expenses.findIndex(e => e.id === id);
        if (index !== -1) {
            this.expenses[index] = { ...this.expenses[index], ...data };
            this.updateGroupedExpenses();
        }
        return Promise.resolve();
    }

    private updateGroupedExpenses() {
        // valid simple grouping logic for the mock
        const groups: { [date: string]: GroupedExpense } = {};

        this.expenses.forEach(e => {
            const date = e.date; // already YYYY-MM-DD string
            if (!groups[date]) {
                groups[date] = { date, items: [], total: 0 };
            }
            groups[date].items.push(e);
            groups[date].total += +e.amount;
        });

        this.groupedExpensesSubject.next(Object.values(groups));
    }
}
